import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { catchError, finalize, forkJoin, interval, Observable, of, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DeliveryGpsHistoryWindow, DeliveryGpsTrackingSnapshot, GpsLocation, GpsLocationHistoryPoint, GpsSelectOption, GpsTrackingSource, GpsVehicleEvent, GpsVehicleLive } from 'src/app/fleet/gps-tracking/gps-tracking.model';
import { GpsTrackingService } from 'src/app/fleet/gps-tracking/gps-tracking.service';
import { GpsMarkerAnimator } from 'src/app/fleet/gps-tracking/gps-marker-animator';
import { gpsTruckMarkerIcon } from 'src/app/fleet/gps-tracking/gps-truck-marker';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { ShipmentService } from 'src/app/shipment/shipment.service';
import { ShipmentDeliveryAttachment } from 'src/app/shipment/models/shipment-delivery.attachment';
import { ShipmentDelivery } from 'src/app/shipment/models/delivery/shipment-delivery.model';
import { Shipment } from 'src/app/shipment/models/shipment';
import { ShipmentDeliveryOperationStatus, ShipmentOperationOrderReleaseStatus, ShipmentOperationTimelineEvent } from 'src/app/shipment/models/workflow/shipment-workflow.model';
import { PosmService } from '../../posm.service';
import { PendingActionTracker } from 'src/app/shared/state/pending-action-tracker';
import { PosmProject, PosmWorkspaceSummary } from '../../models/posm-project.model';
import {
    PosmDeliveryNote,
    PosmDeliverySchedule,
    PosmLinkedReleaseDelivery,
    PosmExecutionReport,
    PosmInstallationTask,
    PosmInstallationTaskEvidence,
    PosmProjectStore,
    PosmStoreChangeRequest
} from '../../models/posm-execution.models';

type ReviewContext =
    | { type: 'EVIDENCE'; evidence: PosmInstallationTaskEvidence; approved: boolean }
    | { type: 'DELAY'; task: PosmInstallationTask; approved: boolean }
    | { type: 'STORE_CHANGE'; change: PosmStoreChangeRequest; approvalType: 'OPS' | 'SALES'; approved: boolean };

interface PosmExecutionFileRow {
    id: string;
    sourceType: 'SHIPMENT';
    shipmentId?: number;
    shipmentNumber?: string;
    deliveryId?: number;
    deliveryNumber?: string;
    storeName?: string;
    city?: string;
    fileName?: string;
    fileUrl?: string;
    fileType?: string;
    fileSize?: number;
    creationDate?: number;
}

interface PosmTrackingLink {
    orderReleases: PosmLinkedReleaseDelivery[];
    tripTypes: string[];
    key: string;
    shipmentId?: number;
    shipmentNumber?: string;
    shipmentStatus?: string;
    deliveryId?: number;
    deliveryNumber?: string;
    deliveryStatus?: string;
    transportStatus?: string;
    vehicleId?: number;
    vehicleLabel?: string;
    driverName?: string;
    storeNames: string[];
    cityNames: string[];
    releaseNumbers: string[];
    releaseStatuses: string[];
    files: PosmExecutionFileRow[];
    schedules: PosmDeliverySchedule[];
    shipment?: Shipment;
    delivery?: ShipmentDelivery;
}

interface PosmReleaseGroup {
    key: string;
    releaseNumber: string;
    releaseStatus: string;
    schedules: PosmDeliverySchedule[];
}

interface PosmStatusUpdate {
    id: string;
    title: string;
    subtitle: string;
    status?: string;
    timestamp?: number;
    source: 'TIMELINE' | 'STATUS_HISTORY';
}

@Component({
    selector: 'app-posm-project-execution',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        GoogleMapsModule,
        ButtonModule,
        DatePickerModule,
        DialogModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        PageDataPanelComponent,
        PageMetricStripComponent,
        SelectModule,
        TableModule,
        TagModule,
        TextareaModule,
        ToastModule,
        TooltipModule
    ],
    providers: [MessageService],
    templateUrl: './posm-project-execution.component.html',
    styleUrls: ['./posm-project-execution.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectExecutionComponent implements OnInit, OnDestroy {
    public readonly reportActions = new PendingActionTracker<string>();
    public project = input.required<PosmProject>();
    public workspaceSummary = input<PosmWorkspaceSummary | null>(null);

    private readonly shipmentService = inject(ShipmentService);
    private readonly gpsTrackingService = inject(GpsTrackingService);
    private readonly posmService = inject(PosmService);
    private readonly session = inject(SessionStorageService);
    private readonly messageService = inject(MessageService);

    public readonly shipments = signal<Shipment[]>([]);
    public readonly deliverySchedules = signal<PosmDeliverySchedule[]>([]);
    public readonly fieldTasks = signal<PosmInstallationTask[]>([]);
    public readonly stores = signal<PosmProjectStore[]>([]);
    public readonly deliveryNotes = signal<PosmDeliveryNote[]>([]);
    public readonly storeChanges = signal<PosmStoreChangeRequest[]>([]);
    public readonly trackingLinks = signal<PosmTrackingLink[]>([]);
    public readonly trackingLoadFailed = signal(false);
    private workspaceLoadVersion = 0;
    public readonly linkedFiles = signal<PosmExecutionFileRow[]>([]);
    public readonly selectedTaskEvidence = signal<PosmInstallationTaskEvidence[]>([]);
    public readonly selectedOperationStatus = signal<ShipmentDeliveryOperationStatus | null>(null);
    public readonly selectedStatusHistory = signal<Record<string, number>>({});
    public readonly selectedExecutionReport = signal<PosmExecutionReport | null>(null);
    public readonly fleetLiveMap = signal<Map<number, GpsVehicleLive>>(new Map());
    public readonly gpsSources = signal<GpsTrackingSource[]>([]);
    public readonly gpsLive = signal<GpsVehicleLive | null>(null);
    public readonly gpsEvents = signal<GpsVehicleEvent[]>([]);
    public readonly gpsHistory = signal<GpsLocationHistoryPoint[]>([]);
    public readonly gpsHistoryWindow = signal<DeliveryGpsHistoryWindow | null>(null);
    public readonly alerts = signal<string[]>([]);
    public readonly liveTrackingEta = signal<string | null>(null);
    public readonly liveTrackingDistance = signal<string | null>(null);
    public readonly isLoading = signal(false);
    public readonly gpsLoading = signal(false);
    public readonly workspaceWarnings = signal<string[]>([]);
    public readonly storeChangesWarning = signal<string | null>(null);

    public readonly selectedTask = signal<PosmInstallationTask | null>(null);
    public readonly selectedDeliveryNote = signal<PosmDeliveryNote | null>(null);
    public readonly reviewContext = signal<ReviewContext | null>(null);

    public readonly taskDialogVisible = signal(false);
    public readonly evidenceDialogVisible = signal(false);
    public readonly completionDialogVisible = signal(false);
    public readonly deliveryNoteDialogVisible = signal(false);
    public readonly storeChangeDialogVisible = signal(false);
    public readonly reviewDialogVisible = signal(false);
    public readonly selectedTrackingKey = signal<string | null>(null);

    public selectedGpsSourceKey = '';
    public gpsPollingEnabled = true;
    public replayIndex = 0;
    public replayPlaying = false;
    public replaySpeed = 1;
    public currentReplayPoint?: GpsLocationHistoryPoint;
    public pickupLocation?: google.maps.LatLngLiteral;
    public dropoffLocation?: google.maps.LatLngLiteral;
    public gpsMapCenter: google.maps.LatLngLiteral = { lat: 24.7136, lng: 46.6753 };
    public gpsMapZoom = 12;
    public options: google.maps.MapOptions = {
        mapTypeId: 'roadmap',
        zoomControl: true,
        scrollwheel: true,
        disableDoubleClickZoom: true,
        maxZoom: 15,
        minZoom: 8
    };

    public readonly evidenceTypeOptions = [
        { label: 'Before Photo', value: 'BEFORE_PHOTO' },
        { label: 'After Photo', value: 'AFTER_PHOTO' },
        { label: 'Store Signature', value: 'SIGNATURE' },
        { label: 'Store Document', value: 'STORE_DOCUMENT' },
        { label: 'Delay Proof', value: 'DELAY_PROOF' },
        { label: 'Other', value: 'OTHER' }
    ];

    public readonly dnTypeOptions = [
        { label: 'Soft Copy', value: 'SOFT_COPY' },
        { label: 'Hard Copy', value: 'HARD_COPY' }
    ];

    public readonly reportTypeOptions = [
        { label: 'Daily', value: 'DAILY' },
        { label: 'Weekly', value: 'WEEKLY' }
    ];

    public readonly replaySpeeds: GpsSelectOption<number>[] = [
        { label: '1x', value: 1 },
        { label: '2x', value: 2 },
        { label: '10x', value: 10 },
        { label: '100x', value: 100 }
    ];

    public evidenceForm = {
        evidenceType: 'AFTER_PHOTO',
        fileName: '',
        fileUrl: '',
        fileType: '',
        fileSize: null as number | null,
        description: '',
        latitude: null as number | null,
        longitude: null as number | null
    };

    public completionForm = {
        latitude: null as number | null,
        longitude: null as number | null,
        photoUrl: '',
        signatureUrl: '',
        remarks: ''
    };

    public deliveryNoteForm = {
        dnType: 'SOFT_COPY',
        signatureUrl: '',
        signedAt: null as Date | null
    };

    public storeChangeForm = {
        storeId: null as number | null,
        requestedStoreNumber: '',
        requestedCity: '',
        requestedContactName: '',
        requestedContactPhone: '',
        requestedPlannedDate: null as Date | null,
        requestedAddress: '',
        requestedLatitude: null as number | null,
        requestedLongitude: null as number | null,
        reason: ''
    };

    public reviewForm = {
        comment: ''
    };

    public readonly selectedTrackingLink = computed(() =>
        this.trackingLinks().find(link => link.key === this.selectedTrackingKey()) ?? null
    );

    public readonly trackingLinkOptions = computed(() =>
        this.trackingLinks().map(link => ({
            label: this.trackingLinkOptionLabel(link),
            value: link.key
        }))
    );

    public readonly overallProgress = computed(() => {
        const schedules = this.deliverySchedules();
        if (schedules.length) {
            const completed = schedules.filter(schedule => this.isCompletedSchedule(schedule)).length;
            return Math.round((completed / schedules.length) * 100);
        }
        const tasks = this.fieldTasks();
        if (!tasks.length) return 0;
        const completed = tasks.filter(task => this.isCompletedTaskStatus(task.status)).length;
        return Math.round((completed / tasks.length) * 100);
    });

    public readonly pendingEvidenceReviews = computed(() =>
        this.fieldTasks().filter(task => ['PENDING_REVIEW', 'PENDING'].includes(String(task.evidenceApprovalStatus || '').toUpperCase()) && (task.evidenceCount || 0) > 0)
    );

    public readonly storeOptions = computed(() =>
        this.stores().map(store => ({
            label: this.storeLabel(store),
            value: store.id
        }))
    );

    public readonly liveLinkedTrucks = computed(() =>
        this.trackingLinks().filter(link => link.vehicleId && this.fleetLiveMap().has(link.vehicleId)).length
    );

    public readonly selectedReleaseGroups = computed<PosmReleaseGroup[]>(() => {
        const link = this.selectedTrackingLink();
        if (!link) return [];
        const groups = new Map<string, PosmReleaseGroup>();
        link.orderReleases.forEach(release => {
            const key = `release:${release.rootReleaseId || release.releaseId}`;
            const group = groups.get(key) ?? {
                key, releaseNumber: release.rootReleaseNumber || release.releaseNumber || `Release #${release.releaseId}`,
                releaseStatus: release.rootReleaseStatus || release.releaseStatus || 'PENDING', schedules: []
            };
            this.schedulesForReleases([release], link.schedules).forEach(schedule => {
                if (!group.schedules.some(row => row.id === schedule.id)) group.schedules.push(schedule);
            });
            groups.set(key, group);
        });
        return Array.from(groups.values()).sort((a, b) => a.releaseNumber.localeCompare(b.releaseNumber));
    });

    public readonly deliveryStatusUpdates = computed<PosmStatusUpdate[]>(() => {
        const timeline = (this.selectedOperationStatus()?.timelineEvents || []).map((event, index) => ({
            id: `timeline-${event.eventId || index}`,
            title: this.workflowEventLabel(event),
            subtitle: this.workflowEventMeta(event),
            status: event.nextState || event.title || undefined,
            timestamp: this.timeValue(event.eventAt),
            source: 'TIMELINE' as const
        }));
        const statusHistory = Object.entries(this.selectedStatusHistory()).map(([status, value]) => ({
            id: `history-${status}`,
            title: this.formatStatusLabel(status),
            subtitle: 'Delivery status update',
            status,
            timestamp: this.timeValue(value),
            source: 'STATUS_HISTORY' as const
        }));
        return [...timeline, ...statusHistory]
            .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    });

    public readonly metrics = computed<PageMetric[]>(() => [
        {
            label: 'Planned Orders',
            value: this.deliverySchedules().length,
            hint: `${this.overallProgress()}% delivered / completed`,
            tone: 'info'
        },
        {
            label: 'Evidence Review',
            value: this.pendingEvidenceReviews().length,
            hint: 'Tasks awaiting photo/signature approval',
            tone: this.pendingEvidenceReviews().length ? 'warn' : 'success'
        },
        {
            label: 'Linked Deliveries',
            value: this.trackingLinks().filter(link => !!link.deliveryId).length,
            hint: 'Deliveries linked to execution order releases',
            tone: this.trackingLinks().length ? 'info' : 'default'
        },
        {
            label: 'Files',
            value: this.linkedFiles().length,
            hint: 'Shipment-level attachments',
            tone: this.linkedFiles().length ? 'success' : 'default'
        }
    ]);

    private gpsPollSub?: Subscription;
    private replayTimer?: ReturnType<typeof setTimeout>;
    private followGpsMarker = false;
    private readonly gpsMarkerAnimator = new GpsMarkerAnimator(position => {
        if (this.followGpsMarker) {
            this.gpsMapCenter = position;
        }
    });

    ngOnInit(): void {
        this.loadWorkspace();
    }

    ngOnDestroy(): void {
        ++this.workspaceLoadVersion;
        this.gpsPollSub?.unsubscribe();
        this.pauseReplay();
        this.gpsMarkerAnimator.destroy();
    }

    loadWorkspace(): void {
        const projectId = this.project().id;
        const companyId = this.companyId();
        if (!projectId || !companyId) return;

        const loadVersion = ++this.workspaceLoadVersion;
        this.isLoading.set(true);
        this.trackingLoadFailed.set(false);
        this.workspaceWarnings.set([]);
        this.storeChangesWarning.set(null);

        const warnings: string[] = [];

        // Tracking can render before the optional schedule/task workspace loads.
        this.posmService.getLinkedReleaseDeliveries(projectId, companyId).subscribe({
            next: rows => {
                if (loadVersion !== this.workspaceLoadVersion) return;
                this.resolveLinkedDeliveryContext(rows || [], loadVersion);
            },
            error: error => {
                if (loadVersion !== this.workspaceLoadVersion) return;
                this.trackingLoadFailed.set(true);
                this.workspaceWarnings.update(items => [...items, error?.error?.message || 'Linked shipments and deliveries could not be loaded.']);
                this.resolveLinkedDeliveryContext([], loadVersion);
            }
        });

        forkJoin({
            schedules: this.posmService.getDeliverySchedules(projectId, companyId).pipe(
                catchError(() => {
                    warnings.push('POSM planned-order transport links are temporarily unavailable.');
                    return of([] as PosmDeliverySchedule[]);
                })
            ),
            tasks: this.posmService.getExecutionTasksByProject(projectId, companyId).pipe(
                catchError(() => {
                    warnings.push('Field tasks are temporarily unavailable.');
                    return of([] as PosmInstallationTask[]);
                })
            ),
            stores: this.posmService.getStoresByProject(projectId, companyId).pipe(
                catchError(() => {
                    warnings.push('Store list is temporarily unavailable.');
                    return of([] as PosmProjectStore[]);
                })
            ),
            notes: this.posmService.getDeliveryNotesByProject(projectId, companyId).pipe(
                catchError(() => {
                    warnings.push('Delivery notes are temporarily unavailable.');
                    return of([] as PosmDeliveryNote[]);
                })
            ),
            changes: this.posmService.getStoreChangeRequests(projectId, companyId).pipe(
                catchError(() => {
                    const message = 'Store change requests are temporarily unavailable.';
                    warnings.push(message);
                    this.storeChangesWarning.set(message);
                    return of([] as PosmStoreChangeRequest[]);
                })
            )
        }).subscribe({
            next: ({ schedules, tasks, stores, notes, changes }) => {
                if (loadVersion !== this.workspaceLoadVersion) return;
                this.deliverySchedules.set(schedules || []);
                this.fieldTasks.set(tasks || []);
                this.stores.set(stores || []);
                this.deliveryNotes.set(notes || []);
                this.storeChanges.set(changes || []);
                this.workspaceWarnings.update(items => [...new Set([...items, ...warnings])]);
                const currentLinks = this.trackingLinks();
                const details = new Map<number, Shipment>();
                currentLinks.forEach(link => { if (link.deliveryId && link.shipment) details.set(link.deliveryId, link.shipment); });
                const enriched = this.buildTrackingLinks(currentLinks.flatMap(link => link.orderReleases), schedules || [], details);
                this.trackingLinks.set(enriched);
                this.linkedFiles.set(this.buildLinkedFiles(enriched));

            },
            error: () => {
                if (loadVersion !== this.workspaceLoadVersion) return;
                this.workspaceWarnings.update(items => [...items, 'POSM execution workspace is temporarily unavailable.']);
            }
        });
    }

    private resolveLinkedDeliveryContext(rows: PosmLinkedReleaseDelivery[], loadVersion = this.workspaceLoadVersion): void {
        const detailByDeliveryId = new Map<number, Shipment>();
        const render = () => {
            if (loadVersion !== this.workspaceLoadVersion) return;
            const links = this.buildTrackingLinks(rows, this.deliverySchedules(), detailByDeliveryId);
            this.shipments.set([...new Map([...detailByDeliveryId.values()].map(shipment => [shipment.shipmentId, shipment])).values()]);
            this.trackingLinks.set(links);
            this.linkedFiles.set(this.buildLinkedFiles(links));
            this.ensureSelectedTrackingLink();
            this.isLoading.set(false);
        };
        // The endpoint already verified each shipment/delivery relationship.
        // Optional detail requests enrich the rows and cannot remove them.
        render();
        this.loadFleetSnapshot();
        this.loadSelectedTrackingContext();
        const deliveryIds = [...new Set(rows.map(row => row.deliveryId).filter((id): id is number => !!id))];
        if (!deliveryIds.length) return;
        forkJoin(deliveryIds.map(id => this.shipmentService.getShipmentAndDeliveryByDeliveryId(id)
            .pipe(catchError(() => of(undefined))))).subscribe(results => {
                if (loadVersion !== this.workspaceLoadVersion) return;
                let missingDetails = false;
                results.forEach((shipment, index) => {
                    const id = deliveryIds[index];
                    if (shipment?.deliveries?.some(delivery => delivery.deliveryId === id)
                        && rows.some(row => row.deliveryId === id && row.shipmentId === shipment.shipmentId)) {
                        detailByDeliveryId.set(id, shipment);
                    } else { missingDetails = true; }
                });
                if (missingDetails) this.workspaceWarnings.update(items => [...items,
                    'Some delivery details could not be loaded. Linked shipments and deliveries are still shown.']);
                render();
                const selected = this.selectedTrackingLink();
                if (selected) this.syncSelectedShipmentLocations(selected);
            });
    }

    private schedulesForReleases(releases: PosmLinkedReleaseDelivery[], schedules: PosmDeliverySchedule[]): PosmDeliverySchedule[] {
        const ids = new Set(releases.flatMap(release => release.deliveryScheduleIds || []));
        const releaseIds = new Set(releases.flatMap(release => [release.releaseId, release.rootReleaseId].filter((id): id is number => !!id)));
        return schedules.filter(schedule => !!schedule.id && (ids.has(schedule.id)
            || (schedule.transportReleaseId != null && releaseIds.has(schedule.transportReleaseId))
            || (schedule.removalTransportReleaseId != null && releaseIds.has(schedule.removalTransportReleaseId))
            || schedule.transportReleaseGroups?.some(group => group.transportReleaseId != null && releaseIds.has(group.transportReleaseId))));
    }

    private buildTrackingLinks(rows: PosmLinkedReleaseDelivery[], schedules: PosmDeliverySchedule[], shipmentByDeliveryId: Map<number, Shipment>): PosmTrackingLink[] {
        const groups = new Map<string, PosmTrackingLink>();
        rows.forEach(row => {
            const key = row.deliveryId ? `delivery:${row.deliveryId}` : `shipment:${row.shipmentId}`;
            const shipment = row.deliveryId ? shipmentByDeliveryId.get(row.deliveryId) : undefined;
            const delivery = shipment?.deliveries?.find(item => item.deliveryId === row.deliveryId);
            const link: PosmTrackingLink = groups.get(key) ?? {
                key, shipmentId: row.shipmentId, shipmentNumber: shipment?.shipmentNumber || row.shipmentNumber,
                shipmentStatus: shipment?.workflowState || shipment?.shipmentStatus || row.shipmentStatus,
                deliveryId: row.deliveryId, deliveryNumber: delivery?.deliveryNumber || row.deliveryNumber,
                deliveryStatus: delivery?.workflowState || String(delivery?.jobStatus || '') || row.deliveryStatus,
                transportStatus: row.releaseStatus,
                vehicleId: delivery?.deliveryVehicle?.vehicleId || row.vehicleId,
                vehicleLabel: delivery?.deliveryVehicle?.plateNumber || (row.vehicleId ? `Vehicle #${row.vehicleId}` : undefined),
                driverName: delivery?.deliveryDriver?.legalName || delivery?.deliveryDriver?.name || (row.driverId ? `Driver #${row.driverId}` : undefined),
                storeNames: [], cityNames: [], releaseNumbers: [], releaseStatuses: [], tripTypes: [], orderReleases: [], files: [], schedules: [],
                shipment, delivery
            };
            if (!link.orderReleases.some(release => release.releaseId === row.releaseId)) link.orderReleases.push(row);
            this.pushUnique(link.releaseNumbers, row.rootReleaseNumber || row.releaseNumber || `Release #${row.releaseId}`);
            this.pushUnique(link.releaseStatuses, row.releaseStatus || 'PENDING');
            this.pushUnique(link.tripTypes, this.formatStatusLabel(row.tripType || 'CUSTOMER_DELIVERY'));
            groups.set(key, link);
        });
        groups.forEach(link => {
            link.schedules = this.schedulesForReleases(link.orderReleases, schedules);
            link.schedules.forEach(schedule => {
                this.pushUnique(link.storeNames, this.scheduleStoreName(schedule));
                this.pushUnique(link.cityNames, schedule.city || schedule.store?.city || '-');
            });
            link.files = this.buildLinkFiles(link);
        });
        return [...groups.values()].sort((a, b) => this.trackingLinkOptionLabel(a).localeCompare(this.trackingLinkOptionLabel(b)));
    }

    private buildLinkFiles(link: PosmTrackingLink): PosmExecutionFileRow[] {
        const files = new Map<string, PosmExecutionFileRow>();
        const shipmentFiles = link.shipment?.filesList || [];
        const primarySchedule = link.schedules[0];

        shipmentFiles.filter(file => !file.deliveryId || file.deliveryId === link.deliveryId).forEach(file => {
            const row = this.mapAttachment(file, link, primarySchedule);
            files.set(row.id, row);
        });

        return Array.from(files.values()).sort((a, b) => Number(b.creationDate || 0) - Number(a.creationDate || 0));
    }

    private mapAttachment(
        file: ShipmentDeliveryAttachment,
        link: PosmTrackingLink,
        schedule?: PosmDeliverySchedule
    ): PosmExecutionFileRow {
        return {
            id: `SHIPMENT:${file.attachmentId || file.fileUrl || file.fileName}`,
            sourceType: 'SHIPMENT',
            shipmentId: link.shipmentId,
            shipmentNumber: link.shipmentNumber,
            deliveryId: link.deliveryId,
            deliveryNumber: link.deliveryNumber,
            storeName: schedule ? this.scheduleStoreName(schedule) : link.storeNames[0],
            city: schedule?.city || link.cityNames[0],
            fileName: file.fileName,
            fileUrl: file.fileUrl,
            fileType: file.fileType,
            fileSize: file.fileSize,
            creationDate: file.creationDate
        };
    }

    private buildLinkedFiles(links: PosmTrackingLink[]): PosmExecutionFileRow[] {
        const items = new Map<string, PosmExecutionFileRow>();
        links.forEach(link => link.files.forEach(file => items.set(file.id, file)));
        return Array.from(items.values()).sort((a, b) => Number(b.creationDate || 0) - Number(a.creationDate || 0));
    }

    private ensureSelectedTrackingLink(): void {
        const keys = this.trackingLinks().map(link => link.key);
        if (!keys.length) {
            this.selectedTrackingKey.set(null);
            return;
        }
        if (this.selectedTrackingKey() && keys.includes(this.selectedTrackingKey()!)) {
            return;
        }
        const preferred = this.trackingLinks().find(link => !!link.vehicleId || !!link.deliveryId) ?? this.trackingLinks()[0];
        this.selectedTrackingKey.set(preferred.key);
    }

    onTrackingLinkChange(key: string | null): void {
        this.selectedTrackingKey.set(key);
        this.loadSelectedTrackingContext();
    }

    refreshTracking(): void {
        this.loadWorkspace();
    }

    private loadFleetSnapshot(): void {
        const companyId = this.companyId();
        const vehicleIds = new Set(this.trackingLinks().map(link => link.vehicleId).filter((value): value is number => !!value));
        if (!companyId || !vehicleIds.size) {
            this.fleetLiveMap.set(new Map());
            return;
        }
        this.gpsTrackingService.getCompanyVehicleLive(companyId, this.selectedGpsSourceKey || undefined).pipe(
            catchError(() => of([] as GpsVehicleLive[]))
        ).subscribe(items => {
            const liveMap = new Map<number, GpsVehicleLive>();
            items.filter(item => vehicleIds.has(item.vehicleId)).forEach(item => liveMap.set(item.vehicleId, item));
            this.fleetLiveMap.set(liveMap);
        });
    }

    loadSelectedTrackingContext(showSpinner = true): void {
        const link = this.selectedTrackingLink();
        const companyId = this.companyId();
        if (!link) {
            this.resetSelectedTrackingState();
            return;
        }

        if (showSpinner) {
            this.pauseReplay();
        }
        this.syncSelectedShipmentLocations(link);
        const shipmentId = link.shipmentId;
        const deliveryId = link.deliveryId;

        if (showSpinner) {
            this.gpsLoading.set(true);
        }

        forkJoin({
            fleet: companyId
                ? this.gpsTrackingService.getCompanyVehicleLive(companyId, this.selectedGpsSourceKey || undefined).pipe(catchError(() => of([] as GpsVehicleLive[])))
                : of([] as GpsVehicleLive[]),
            tracking: shipmentId && deliveryId
                ? this.shipmentService.getDeliveryGpsTracking(shipmentId, deliveryId, this.selectedGpsSourceKey || undefined)
                    .pipe(catchError(() => of(undefined as DeliveryGpsTrackingSnapshot | undefined)))
                : of(undefined as DeliveryGpsTrackingSnapshot | undefined),
            operationStatus: shipmentId && deliveryId
                ? this.shipmentService.getDeliveryOperationStatus(shipmentId, deliveryId).pipe(catchError(() => of(null)))
                : of(null),
            statusHistory: shipmentId && deliveryId
                ? this.shipmentService.getDeliveryStatusHistory(shipmentId, deliveryId).pipe(catchError(() => of(new Map<string, number>())))
                : of(new Map<string, number>()),
            eta: deliveryId
                ? this.shipmentService.getDeliveryTrackingEta(deliveryId).pipe(catchError(() => of(undefined)))
                : of(undefined)
        }).pipe(finalize(() => this.gpsLoading.set(false))).subscribe({
            next: ({ fleet, tracking, operationStatus, statusHistory, eta }) => {
                this.gpsSources.set(tracking?.sources || []);
                this.gpsLive.set(tracking?.live || null);
                this.gpsEvents.set(this.sortGpsEvents(tracking?.events || []));
                this.gpsHistory.set(this.sortGpsHistory(tracking?.history || []));
                this.gpsHistoryWindow.set(tracking?.historyWindow || null);
                this.selectedOperationStatus.set(operationStatus);
                this.selectedStatusHistory.set(this.normalizeStatusHistory(statusHistory));
                if (showSpinner) {
                    this.resetReplay(false);
                } else {
                    this.syncReplayPointAfterHistoryRefresh();
                }
                if (!this.currentReplayPoint) {
                    this.moveGpsMarker(this.gpsLocationPosition(tracking?.live?.location), false, 700, tracking?.live?.location);
                }
                if (showSpinner) {
                    this.centerGpsMap();
                }
                const liveMap = new Map<number, GpsVehicleLive>();
                fleet.filter(item => !!item.vehicleId).forEach(item => liveMap.set(item.vehicleId, item));
                this.fleetLiveMap.set(liveMap);

                if (eta) {
                    this.liveTrackingEta.set(eta.duration || null);
                    this.liveTrackingDistance.set(eta.distance || null);
                    this.checkAlerts(eta);
                } else {
                    this.liveTrackingEta.set(null);
                    this.liveTrackingDistance.set(null);
                    this.alerts.set([]);
                }
                this.configureGpsPolling();
            }
        });
    }

    private resetSelectedTrackingState(): void {
        this.gpsMarkerAnimator.snapTo(null);
        this.gpsSources.set([]);
        this.gpsLive.set(null);
        this.gpsEvents.set([]);
        this.gpsHistory.set([]);
        this.gpsHistoryWindow.set(null);
        this.selectedOperationStatus.set(null);
        this.selectedStatusHistory.set({});
        this.liveTrackingEta.set(null);
        this.liveTrackingDistance.set(null);
        this.alerts.set([]);
        this.pauseReplay();
        this.gpsPollSub?.unsubscribe();
    }

    private syncSelectedShipmentLocations(link: PosmTrackingLink): void {
        const source = link.shipment?.sourceLocation?.location;
        const destination = link.shipment?.destinationLocation?.location;
        this.pickupLocation = this.latLng(source?.latitude, source?.longitude);
        this.dropoffLocation = this.latLng(destination?.latitude, destination?.longitude);
    }

    onGpsSourceChange(): void {
        this.loadFleetSnapshot();
        this.loadSelectedTrackingContext();
    }

    toggleGpsPolling(): void {
        this.gpsPollingEnabled = !this.gpsPollingEnabled;
        this.configureGpsPolling();
    }

    gpsSourceOptions(): GpsSelectOption[] {
        return [
            { label: 'Best available', value: '' },
            ...this.gpsSources().map(item => ({
                label: this.displayGpsName(item.displayName || item.sourceKey),
                value: item.sourceKey
            }))
        ];
    }

    gpsHistoryWindowLabel(): string {
        const window = this.gpsHistoryWindow();
        if (!window?.available || !window.from || !window.to) {
            return window?.status === 'DELIVERY_NOT_STARTED'
                ? 'Unavailable — delivery has not entered In Progress'
                : 'Unavailable';
        }
        return `${formatDate(window.from, 'dd MMM yyyy, HH:mm', 'en-US')} - ${formatDate(window.to, 'dd MMM yyyy, HH:mm', 'en-US')}`;
    }

    gpsRoutePath(): google.maps.LatLngLiteral[] {
        return this.gpsHistory()
            .map(point => this.gpsPointPosition(point))
            .filter((value): value is google.maps.LatLngLiteral => !!value);
    }

    gpsLiveMarkerPosition(): google.maps.LatLngLiteral | null {
        return this.gpsMarkerAnimator.position() ?? this.gpsLocationPosition(this.gpsLive()?.location);
    }

    gpsDisplayLocation(): GpsLocation | GpsLocationHistoryPoint | null {
        return this.gpsLive()?.location || this.gpsHistory()[this.gpsHistory().length - 1] || null;
    }

    replayMarkerPosition(): google.maps.LatLngLiteral | null {
        return this.currentReplayPoint ? this.gpsMarkerAnimator.position() : null;
    }

    gpsVehicleMarkerOptions(selected = true): google.maps.MarkerOptions {
        return {
            icon: gpsTruckMarkerIcon({
                headingDegrees: this.gpsMarkerAnimator.headingDegrees(),
                selected,
                stale: !this.currentReplayPoint && this.gpsLive()?.liveStatus === 'STALE_LOCATION'
            }),
            zIndex: selected ? 30 : 20
        };
    }

    startReplay(): void {
        if (!this.gpsHistory().length) return;
        if (this.replayIndex >= this.replayMaxIndex()) {
            this.setReplayIndex(0, false);
        }
        this.replayPlaying = true;
        this.scheduleReplayStep();
    }

    pauseReplay(): void {
        this.replayPlaying = false;
        if (this.replayTimer) {
            clearTimeout(this.replayTimer);
            this.replayTimer = undefined;
        }
    }

    resetReplay(shouldCenter = true): void {
        this.pauseReplay();
        this.setReplayIndex(0, shouldCenter);
    }

    onReplayIndexChange(value: number | string): void {
        this.pauseReplay();
        this.setReplayIndex(Number(value), true);
    }

    onReplaySpeedChange(): void {
        if (this.replayPlaying) {
            this.scheduleReplayStep();
        }
    }

    replayProgress(): number {
        const history = this.gpsHistory();
        if (history.length <= 1) return history.length ? 100 : 0;
        return Math.round((this.replayIndex / (history.length - 1)) * 100);
    }

    replayMaxIndex(): number {
        return Math.max(this.gpsHistory().length - 1, 0);
    }

    derivedGpsAlerts(): GpsVehicleEvent[] {
        const alertTypes = new Set([
            'GPS_SIGNAL_LOST',
            'GPS_SIGNAL_RESTORED',
            'GPS_SIGNAL_ACQUIRED',
            'IGNITION_ON',
            'IGNITION_OFF',
            'MOVING',
            'STOPPED'
        ]);
        return this.gpsEvents().filter(event => alertTypes.has(event.eventType));
    }

    gpsStatusLabel(status?: string | null): string {
        switch (status) {
            case 'ACTIVE_LOCATION': return 'Active';
            case 'STALE_LOCATION': return 'Stale';
            case 'NO_GPS_MAPPING': return 'No mapping';
            case 'NO_LOCATION': return 'No location';
            default: return status ? this.formatStatusLabel(status) : 'Unavailable';
        }
    }

    gpsStatusSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACTIVE_LOCATION': return 'success';
            case 'STALE_LOCATION': return 'warn';
            case 'NO_GPS_MAPPING':
            case 'NO_LOCATION': return 'danger';
            default: return 'secondary';
        }
    }

    gpsEventSeverity(eventType?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (eventType) {
            case 'GPS_SIGNAL_LOST': return 'danger';
            case 'GPS_SIGNAL_RESTORED':
            case 'GPS_SIGNAL_ACQUIRED': return 'success';
            case 'MOVING':
            case 'IGNITION_ON': return 'info';
            case 'STOPPED':
            case 'IGNITION_OFF': return 'secondary';
            default: return 'warn';
        }
    }

    gpsSourceName(sourceKey?: string | null): string {
        if (!sourceKey) return 'Best available';
        const source = this.gpsSources().find(item => item.sourceKey === sourceKey);
        return this.displayGpsName(source?.displayName ?? sourceKey);
    }

    gpsCoordinateLabel(location?: { latitude?: number | string | null; longitude?: number | string | null } | null): string {
        const lat = Number(location?.latitude);
        const lng = Number(location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '-';
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }

    gpsSpeedLabel(location?: { speedKph?: number | string | null } | null): string {
        const speed = Number(location?.speedKph ?? 0);
        return `${speed.toFixed(1)} kph`;
    }

    gpsDateLabel(value?: string | number | Date | null): string {
        if (!value) return '-';
        return formatDate(value, 'dd MMM yyyy, HH:mm', 'en-US');
    }

    displayGpsName(value?: string | null): string {
        if (!value) return '-';
        return value
            .replace(/PROLOGIX/g, 'CARGOMATE')
            .replace(/ProLogix/g, 'CargoMate')
            .replace(/Prologix/g, 'CargoMate')
            .replace(/prologix/g, 'CargoMate');
    }

    workflowEventLabel(event: ShipmentOperationTimelineEvent): string {
        return event.title || event.nextState || event.description || 'Workflow update';
    }

    workflowEventMeta(event: ShipmentOperationTimelineEvent): string {
        const parts = [event.level, event.previousState ? `From ${this.formatStatusLabel(event.previousState)}` : '', event.description || '']
            .filter(Boolean);
        return parts.join(' | ') || 'Operational workflow update';
    }

    currentPhaseLabel(): string {
        return this.selectedOperationStatus()?.currentPhase || this.selectedTrackingLink()?.transportStatus || 'No active phase';
    }

    shipmentWorkflowState(): string {
        return this.selectedOperationStatus()?.shipmentStatus || this.selectedTrackingLink()?.shipmentStatus || 'UNKNOWN';
    }

    deliveryWorkflowState(): string {
        return this.selectedOperationStatus()?.deliveryStatus || this.selectedTrackingLink()?.deliveryStatus || 'UNKNOWN';
    }

    activeOrNextStopLabel(): string {
        const stop = this.selectedOperationStatus()?.activeStop || this.selectedOperationStatus()?.nextStop;
        if (!stop) return 'No pending site';
        const prefix = stop.active ? 'Active' : 'Next';
        return `${prefix} ${this.formatStatusLabel(stop.stopType || 'stop')} #${stop.stopNumber || '-'}`;
    }

    pickupProgressLabel(): string {
        const progress = this.selectedOperationStatus()?.pickupProgress;
        if (!progress) return 'Pickup progress unavailable';
        return `Pickup ${progress.completed ?? 0}/${progress.total ?? 0} complete`;
    }

    dropoffProgressLabel(): string {
        const progress = this.selectedOperationStatus()?.dropoffProgress;
        if (!progress) return 'Dropoff progress unavailable';
        return `Dropoff ${progress.completed ?? 0}/${progress.total ?? 0} complete`;
    }

    trackingLinkOptionLabel(link: PosmTrackingLink): string {
        const truck = link.vehicleLabel || 'Unassigned truck';
        const delivery = link.deliveryNumber || (link.deliveryId ? `Delivery #${link.deliveryId}` : link.shipmentNumber || `Shipment #${link.shipmentId}`);
        const store = link.storeNames[0] || 'Order releases';
        return `${truck} | ${delivery} | ${store}`;
    }

    trackingLinkStoreSummary(link: PosmTrackingLink): string {
        return link.storeNames.slice(0, 2).join(', ') + (link.storeNames.length > 2 ? ` +${link.storeNames.length - 2}` : '');
    }

    trackingLinkReleaseSummary(link: PosmTrackingLink): string {
        return link.releaseNumbers.slice(0, 2).join(', ') + (link.releaseNumbers.length > 2 ? ` +${link.releaseNumbers.length - 2}` : '');
    }

    selectedLinkLive(): GpsVehicleLive | null {
        const vehicleId = this.selectedTrackingLink()?.vehicleId;
        if (!vehicleId) return null;
        return this.fleetLiveMap().get(vehicleId) ?? null;
    }

    trackingLinkLive(link: PosmTrackingLink): GpsVehicleLive | null {
        return link.vehicleId ? (this.fleetLiveMap().get(link.vehicleId) ?? null) : null;
    }

    fileSourceLabel(file: PosmExecutionFileRow): string {
        return `Shipment ${file.shipmentNumber || '#' + (file.shipmentId || '-')}`;
    }

    linkedFileSeverity(fileType?: string): 'success' | 'info' | 'warn' | 'secondary' {
        const normalized = String(fileType || '').toUpperCase();
        if (normalized.includes('PDF')) return 'info';
        if (normalized.includes('IMAGE') || normalized.includes('PNG') || normalized.includes('JPG') || normalized.includes('JPEG')) return 'success';
        if (normalized.includes('DOC')) return 'warn';
        return 'secondary';
    }

    openTask(task: PosmInstallationTask): void {
        this.selectedTask.set(task);
        this.taskDialogVisible.set(true);
        this.loadTaskEvidence(task);
    }

    loadTaskEvidence(task = this.selectedTask()): void {
        const companyId = this.companyId();
        if (!task?.id || !companyId) return;
        this.posmService.getInstallationTaskEvidence(task.id, companyId).subscribe({
            next: evidence => this.selectedTaskEvidence.set(evidence || []),
            error: () => this.selectedTaskEvidence.set([])
        });
    }

    openEvidenceDialog(task: PosmInstallationTask): void {
        this.selectedTask.set(task);
        this.evidenceForm = {
            evidenceType: 'AFTER_PHOTO',
            fileName: '',
            fileUrl: '',
            fileType: '',
            fileSize: null,
            description: '',
            latitude: null,
            longitude: null
        };
        this.evidenceDialogVisible.set(true);
    }

    submitEvidence(): void {
        const task = this.selectedTask();
        const companyId = this.companyId();
        if (!task?.id || !companyId || !this.evidenceForm.fileUrl?.trim()) return;
        this.posmService.addInstallationTaskEvidence(task.id, companyId, {
            evidenceType: this.evidenceForm.evidenceType,
            fileName: this.evidenceForm.fileName || undefined,
            fileUrl: this.evidenceForm.fileUrl.trim(),
            fileType: this.evidenceForm.fileType || undefined,
            fileSize: this.evidenceForm.fileSize || undefined,
            description: this.evidenceForm.description || undefined,
            latitude: this.evidenceForm.latitude || undefined,
            longitude: this.evidenceForm.longitude || undefined,
            uploadedByCustomerId: this.currentCustomerId()
        }).subscribe({
            next: () => {
                this.evidenceDialogVisible.set(false);
                this.loadTaskEvidence(task);
                this.loadWorkspace();
                this.messageService.add({ severity: 'success', summary: 'Evidence Saved', detail: 'Task evidence was added.' });
            }
        });
    }

    openCompletionDialog(task: PosmInstallationTask): void {
        this.selectedTask.set(task);
        this.completionForm = {
            latitude: task.latitude ?? null,
            longitude: task.longitude ?? null,
            photoUrl: task.photoUrl || '',
            signatureUrl: task.signatureUrl || '',
            remarks: task.feedbackNotes || ''
        };
        this.completionDialogVisible.set(true);
    }

    submitCompletion(): void {
        const task = this.selectedTask();
        const companyId = this.companyId();
        if (!task?.id || !companyId) return;
        this.posmService.completeInstallationTaskWorkflow(task.id, companyId, {
            latitude: this.completionForm.latitude || undefined,
            longitude: this.completionForm.longitude || undefined,
            photoUrl: this.completionForm.photoUrl || undefined,
            signatureUrl: this.completionForm.signatureUrl || undefined,
            remarks: this.completionForm.remarks || undefined,
            submittedByCustomerId: this.currentCustomerId()
        }).subscribe({
            next: saved => {
                this.selectedTask.set(saved);
                this.completionDialogVisible.set(false);
                this.loadWorkspace();
                this.loadTaskEvidence(saved);
                this.messageService.add({ severity: 'success', summary: 'Task Completed', detail: 'Completion evidence was submitted for review.' });
            }
        });
    }

    openDeliveryNoteDialog(task: PosmInstallationTask): void {
        this.selectedTask.set(task);
        this.deliveryNoteForm = {
            dnType: 'SOFT_COPY',
            signatureUrl: task.signatureUrl || '',
            signedAt: task.signatureUrl ? new Date() : null
        };
        this.deliveryNoteDialogVisible.set(true);
    }

    submitDeliveryNote(): void {
        const task = this.selectedTask();
        const companyId = this.companyId();
        if (!task?.id || !companyId) return;
        this.posmService.generateTaskDeliveryNote(companyId, {
            taskId: task.id,
            deliveryId: task.deliveryId,
            dnType: this.deliveryNoteForm.dnType,
            signatureUrl: this.deliveryNoteForm.signatureUrl || undefined,
            signedAt: this.deliveryNoteForm.signedAt || undefined,
            generatedByCustomerId: this.currentCustomerId()
        }).subscribe({
            next: note => {
                this.selectedDeliveryNote.set(note);
                this.deliveryNoteDialogVisible.set(false);
                this.loadWorkspace();
                this.messageService.add({ severity: 'success', summary: 'Delivery Note', detail: 'Delivery note was generated.' });
            }
        });
    }

    openStoreChangeDialog(store?: PosmProjectStore): void {
        this.storeChangeForm = {
            storeId: store?.id || null,
            requestedStoreNumber: store?.storeNumber || '',
            requestedCity: store?.city || '',
            requestedContactName: store?.contactPersonName || '',
            requestedContactPhone: store?.contactPersonPhone || '',
            requestedPlannedDate: store?.plannedDate ? new Date(store.plannedDate) : null,
            requestedAddress: store?.location?.address || store?.address || '',
            requestedLatitude: store?.location?.latitude ?? null,
            requestedLongitude: store?.location?.longitude ?? null,
            reason: ''
        };
        this.storeChangeDialogVisible.set(true);
    }

    submitStoreChange(): void {
        const projectId = this.project().id;
        const companyId = this.companyId();
        if (!projectId || !companyId || !this.storeChangeForm.storeId || !this.storeChangeForm.reason?.trim()) return;
        this.posmService.requestStoreChange(projectId, companyId, {
            storeId: this.storeChangeForm.storeId ?? undefined,
            requestedStoreNumber: this.storeChangeForm.requestedStoreNumber || undefined,
            requestedCity: this.storeChangeForm.requestedCity || undefined,
            requestedContactName: this.storeChangeForm.requestedContactName || undefined,
            requestedContactPhone: this.storeChangeForm.requestedContactPhone || undefined,
            requestedPlannedDate: this.storeChangeForm.requestedPlannedDate || undefined,
            requestedAddress: this.storeChangeForm.requestedAddress || undefined,
            requestedLatitude: this.storeChangeForm.requestedLatitude ?? undefined,
            requestedLongitude: this.storeChangeForm.requestedLongitude ?? undefined,
            reason: this.storeChangeForm.reason,
            requestedByCustomerId: this.currentCustomerId()
        }).subscribe({
            next: () => {
                this.storeChangeDialogVisible.set(false);
                this.loadWorkspace();
                this.messageService.add({ severity: 'success', summary: 'Change Requested', detail: 'Store change is pending OPS and Sales approval.' });
            }
        });
    }

    openReviewEvidence(evidence: PosmInstallationTaskEvidence, approved: boolean): void {
        this.reviewForm.comment = '';
        this.reviewContext.set({ type: 'EVIDENCE', evidence, approved });
        this.reviewDialogVisible.set(true);
    }

    openReviewDelay(task: PosmInstallationTask, approved: boolean): void {
        this.reviewForm.comment = '';
        this.reviewContext.set({ type: 'DELAY', task, approved });
        this.reviewDialogVisible.set(true);
    }

    openReviewStoreChange(change: PosmStoreChangeRequest, approvalType: 'OPS' | 'SALES', approved: boolean): void {
        this.reviewForm.comment = '';
        this.reviewContext.set({ type: 'STORE_CHANGE', change, approvalType, approved });
        this.reviewDialogVisible.set(true);
    }

    submitReview(): void {
        const context = this.reviewContext();
        const companyId = this.companyId();
        if (!context || !companyId) return;

        const request = {
            reviewedByCustomerId: this.currentCustomerId(),
            approvedByCustomerId: this.currentCustomerId(),
            comment: this.reviewForm.comment || undefined
        };

        let action$: Observable<unknown>;
        if (context.type === 'EVIDENCE') {
            if (!context.evidence.id) return;
            action$ = context.approved
                ? this.posmService.approveInstallationTaskEvidence(context.evidence.id, companyId, request)
                : this.posmService.rejectInstallationTaskEvidence(context.evidence.id, companyId, request);
        } else if (context.type === 'DELAY') {
            if (!context.task.id) return;
            action$ = context.approved
                ? this.posmService.approveInstallationTaskDelay(context.task.id, companyId, request)
                : this.posmService.rejectInstallationTaskDelay(context.task.id, companyId, request);
        } else {
            if (!context.change.id) return;
            action$ = context.approved
                ? this.posmService.approveStoreChangeRequest(context.change.id, companyId, context.approvalType, request)
                : this.posmService.rejectStoreChangeRequest(context.change.id, companyId, context.approvalType, request);
        }

        action$.subscribe({
            next: () => {
                this.reviewDialogVisible.set(false);
                this.reviewContext.set(null);
                this.loadWorkspace();
                const task = this.selectedTask();
                if (task) this.loadTaskEvidence(task);
                this.messageService.add({ severity: 'success', summary: 'Review Saved', detail: 'Approval decision was recorded.' });
            }
        });
    }

    generateReport(reportType: string): void {
        const projectId = this.project().id;
        const companyId = this.companyId();
        const actionKey = `generate:${reportType}`;
        if (!projectId || !companyId || !this.reportActions.begin(actionKey)) return;
        this.posmService.generateExecutionReport(projectId, companyId, reportType, this.currentCustomerId()).pipe(
            finalize(() => this.reportActions.end(actionKey))
        ).subscribe({
            next: report => {
                this.selectedExecutionReport.set(report);
                this.messageService.add({ severity: 'success', summary: 'Report Generated', detail: `${reportType.toLowerCase()} execution report data was saved.` });
            }
        });
    }

    scheduleStoreName(schedule: PosmDeliverySchedule): string {
        return schedule.storeName || schedule.store?.storeName || schedule.store?.location?.name || schedule.storeNumber || '-';
    }

    taskStoreName(task: PosmInstallationTask): string {
        return task.store?.location?.name || task.storeName || task.store?.storeName || task.store?.storeNumber || '-';
    }

    storeLabel(store: PosmProjectStore): string {
        return store.storeName || store.location?.name || store.storeNumber || `Store #${store.id}`;
    }

    evidenceSeverity(status?: string): 'success' | 'warn' | 'danger' {
        const normalized = String(status || '').toUpperCase();
        if (normalized === 'APPROVED') return 'success';
        if (normalized === 'REJECTED') return 'danger';
        return 'warn';
    }

    taskStatusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' {
        const normalized = String(status || '').toUpperCase();
        if (normalized === 'COMPLETED' || normalized === 'CLOSED') return 'success';
        if (normalized === 'IN_PROGRESS') return 'info';
        if (normalized === 'CANCELLED') return 'danger';
        return 'warn';
    }

    transportSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = String(status || '').toUpperCase();
        if (normalized.includes('COMPLETE') || normalized.includes('DELIVERED')) return 'success';
        if (normalized.includes('TRANSIT') || normalized.includes('ACTIVE') || normalized.includes('RELEASED')) return 'info';
        if (normalized.includes('ERROR') || normalized.includes('FAILED')) return 'danger';
        if (normalized.includes('SCHEDULED') || normalized.includes('PENDING')) return 'warn';
        return 'secondary';
    }

    changeSeverity(status?: string): 'success' | 'warn' | 'danger' {
        const normalized = String(status || '').toUpperCase();
        if (normalized === 'APPLIED') return 'success';
        if (normalized === 'REJECTED') return 'danger';
        return 'warn';
    }

    approvalSeverity(status?: string): 'success' | 'warn' | 'danger' {
        const normalized = String(status || '').toUpperCase();
        if (normalized === 'APPROVED') return 'success';
        if (normalized === 'REJECTED') return 'danger';
        return 'warn';
    }

    progressSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        return this.transportSeverity(status);
    }

    formatStatusLabel(status?: string | null): string {
        return String(status || 'UNKNOWN')
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, value => value.toUpperCase());
    }

    private isCompletedSchedule(schedule: PosmDeliverySchedule): boolean {
        return String(schedule.progressStage || '').toUpperCase() === 'DELIVERED';
    }

    private isCompletedTaskStatus(status?: string | null): boolean {
        const normalized = String(status || '').toUpperCase();
        return normalized === 'COMPLETED' || normalized === 'CLOSED';
    }

    openUrl(url?: string): void {
        if (url) {
            window.open(url, '_blank');
        }
    }

    private checkAlerts(eta: any): void {
        const messages: string[] = [];
        if (Number(eta?.durationValue) > 3600) {
            messages.push('Delivery is experiencing delays');
        }
        if (this.gpsLive()?.liveStatus === 'STALE_LOCATION') {
            messages.push('Latest truck location is stale');
        }
        this.alerts.set(messages);
    }

    private configureGpsPolling(): void {
        this.gpsPollSub?.unsubscribe();
        if (!this.gpsPollingEnabled || !this.gpsHistoryWindow()?.active) {
            return;
        }
        this.gpsPollSub = interval(15000).subscribe(() => {
            if (!document.hidden) {
                this.loadSelectedTrackingContext(false);
            }
        });
    }

    private centerGpsMap(): void {
        const replayPosition = this.gpsPointPosition(this.currentReplayPoint);
        const livePosition = this.gpsLocationPosition(this.gpsLive()?.location);
        const fallback = this.pickupLocation || this.dropoffLocation;
        const position = replayPosition || livePosition || fallback;
        if (position) {
            this.gpsMapCenter = position;
            this.gpsMapZoom = replayPosition || livePosition ? 15 : 11;
        }
    }

    private gpsLocationPosition(location?: GpsLocation | null): google.maps.LatLngLiteral | null {
        const lat = Number(location?.latitude);
        const lng = Number(location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
    }

    gpsPointPosition(point?: GpsLocationHistoryPoint | null): google.maps.LatLngLiteral | null {
        const lat = Number(point?.latitude);
        const lng = Number(point?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
    }

    private setReplayIndex(index: number, shouldCenter: boolean, transitionMs = 700): void {
        const history = this.gpsHistory();
        if (!history.length) {
            this.replayIndex = 0;
            this.currentReplayPoint = undefined;
            this.gpsMarkerAnimator.snapTo(null);
            return;
        }
        const safeIndex = Math.max(0, Math.min(index, this.replayMaxIndex()));
        this.replayIndex = safeIndex;
        this.currentReplayPoint = history[safeIndex];
        this.moveGpsMarker(this.gpsPointPosition(this.currentReplayPoint), shouldCenter, transitionMs, this.currentReplayPoint);
        if (shouldCenter) this.gpsMapZoom = 15;
    }

    private scheduleReplayStep(): void {
        if (this.replayTimer) {
            clearTimeout(this.replayTimer);
        }
        if (!this.replayPlaying) {
            return;
        }
        const history = this.gpsHistory();
        if (this.replayIndex >= history.length - 1) {
            this.pauseReplay();
            return;
        }
        const current = history[this.replayIndex];
        const next = history[this.replayIndex + 1];
        const delay = this.replayDelayMs(current, next);
        this.replayTimer = setTimeout(() => {
            this.setReplayIndex(this.replayIndex + 1, true, Math.min(Math.max(delay * 0.85, 80), 700));
            this.scheduleReplayStep();
        }, delay);
    }

    private moveGpsMarker(
        position: google.maps.LatLngLiteral | null,
        shouldCenter: boolean,
        transitionMs = 700,
        sample?: Pick<GpsLocation, 'headingDegrees' | 'speedKph'> | GpsLocationHistoryPoint | null
    ): void {
        this.followGpsMarker = shouldCenter;
        this.gpsMarkerAnimator.moveTo(position, sample?.headingDegrees, sample?.speedKph, transitionMs);
    }

    private syncReplayPointAfterHistoryRefresh(): void {
        const history = this.gpsHistory();
        if (!history.length) {
            this.pauseReplay();
            this.setReplayIndex(0, false);
            return;
        }
        const at = this.currentReplayPoint?.recordedAt;
        const matching = at ? history.findIndex(point => point.recordedAt === at) : -1;
        this.setReplayIndex(matching >= 0 ? matching : Math.min(this.replayIndex, history.length - 1), false);
    }

    private replayDelayMs(current?: GpsLocationHistoryPoint, next?: GpsLocationHistoryPoint): number {
        const currentTime = this.timeValue(current?.recordedAt);
        const nextTime = this.timeValue(next?.recordedAt);
        const rawDelay = Math.max(nextTime - currentTime, 1000);
        return Math.max(Math.round(rawDelay / Math.max(this.replaySpeed, 1)), 120);
    }

    private sortGpsHistory(points: GpsLocationHistoryPoint[]): GpsLocationHistoryPoint[] {
        return [...points].sort((a, b) => this.timeValue(a.recordedAt) - this.timeValue(b.recordedAt));
    }

    private sortGpsEvents(events: GpsVehicleEvent[]): GpsVehicleEvent[] {
        return [...events].sort((a, b) => this.timeValue(b.eventTime) - this.timeValue(a.eventTime));
    }

    private timeValue(value?: string | number | Date | null): number {
        const time = value ? new Date(value).getTime() : NaN;
        return Number.isFinite(time) ? time : 0;
    }

    releaseOrderStatus(orderRelease: ShipmentOperationOrderReleaseStatus): string {
        return orderRelease.releaseStatus || orderRelease.validationStatus || 'PENDING';
    }

    orderReleaseSeverity(orderRelease: ShipmentOperationOrderReleaseStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        return this.transportSeverity(orderRelease.releaseStatus || orderRelease.validationStatus);
    }

    private latLng(latitude?: number | null, longitude?: number | null): google.maps.LatLngLiteral | undefined {
        const lat = Number(latitude);
        const lng = Number(longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
        return { lat, lng };
    }

    private pushUnique(target: string[], value?: string): void {
        if (!value || target.includes(value)) return;
        target.push(value);
    }


    private normalizeStatusHistory(value: Record<string, number> | Map<string, number> | undefined | null): Record<string, number> {
        if (!value) return {};
        if (value instanceof Map) {
            return Object.fromEntries(value.entries());
        }
        return value;
    }

    private companyId(): number | undefined {
        const company = this.project().owningCompanyId || this.session.getCompany()?.id || (this.session.getCustomer() as any)?.company?.id;
        return company ? Number(company) : undefined;
    }

    private currentCustomerId(): number | undefined {
        return this.session.getCustomer()?.id;
    }
}
